/**
 * premiumLedger.ts — the estate's premium years, and the two shapes of money one can be [D-B46].
 *
 * ONE `insurance_loans` ROW IS ONE POLICY YEAR, and it is recorded on one of two instruments
 * [D-B45]: an **Insurance Premium Advance** (Primelane paid the insurer and is recovering it) or an
 * **Insurance Premium Payment Record** (the driver paid the whole premium; nothing was lent,
 * nothing accrues, nothing is owed). The estate page listed and summed all 308 of them as advances,
 * which asserted of 12 rows — GHS 45,883.96 of premium — a thing that is false about them.
 *
 * AND THE ADVANCE IS THE PREMIUM LESS WHAT THE DRIVER PAID. Owner ruling 2026-09-01: *"when a
 * driver pays part of the premium, the interest is only calculated on the remaining amount advanced
 * by Primelane."* So for a premium `A` and deposits `D`, what Primelane advanced is `A − D` — the
 * figure the "Total advanced" card and the "Advanced" column both name. `A` keeps a column of its
 * own ("Premium") because it is the year's subject and the number the Schedule prints first, and
 * `D` keeps one ("Paid by driver") because a column that nets two figures silently is a column
 * nobody can reconcile.
 *
 * LIFTED OUT OF THE COMPONENTS SO IT CAN BE TESTED, on `boardSubjects.ts`' precedent and for its
 * reason: `vitest.config.ts` matches `src/**\/*.test.ts` under `environment: 'node'`, so a rule
 * living inside a `.tsx` queryFn is a rule nothing can reach. Everything this file decides — the
 * request, the mapping, the instrument, the search, the filter, the buckets and the totals — is a
 * pure function of rows, and both tabs of the page read it.
 *
 * THE INSTRUMENT IS `instrumentFor`'S ANSWER AND NOBODY ELSE'S. It is the server's own two limbs
 * (`lib/agreements/instrument.ts`), and it answers `ADVANCE` for every unknown — including a select
 * that stopped asking for one of the embeds. That asymmetry is why `PREMIUM_YEARS_SELECT` is a
 * pinned literal with a test on it: a dropped embed does not fail, it silently relabels every
 * Record as an advance and puts its premium back in the advanced total.
 *
 * NO `recordPublished` GATE HERE, deliberately [D-B46 R9]. The registry gate on the setup board
 * exists because that surface is about to RAISE a document and must not name an unpublished
 * template. This page names a shape of money — "Advance" / "Paid in full" — not a document title,
 * and `insurance_payment` v1 is published on both tiers anyway.
 */

import { supabase } from '@/integrations/supabase/client';
import { depositTotalOf, type LoanDepositEmbed } from '@/components/agreements/boardSubjects';
import { instrumentFor, type InsuranceInstrument } from '@/lib/agreements/instrument';
import { businessMonthKey } from '@/lib/format';
import { assertComplete as assertReadComplete } from '@/lib/fetchAllRows';

/**
 * THE REQUEST, VERBATIM — the byte-stability pin [D-B46 R9].
 *
 * The two instrument limbs are `insurance_loan_deposits(transactions(amount))` and
 * `insurance_loan_instalments(id)`, spelled exactly as `boardSubjects.ts` spells them: the money is
 * on the TRANSACTION and never on the deposit link row, and the instalments are read for their ids
 * alone because the server's limb is `NOT EXISTS` and a count is all a client can want from it.
 *
 * `insurance_contracts` CARRIES `contract_id` AS WELL AS THE POLICY. The junction is the only path
 * from a loan to its contract, and until this read asked for it the page gave an admin no way to
 * get from a premium row to the contract it belongs to. `insurance_loans` has no FK to `insurances`
 * in the live schema — embedding `insurances(...)` directly is a 400 (Sentry FLEET-ADMIN-A) — so
 * the policy comes through the junction too.
 *
 * `starts_at` IS ON THE POLICY, NOT THE LOAN. It is the year the premium is *for*, which is what
 * `get_contracts_analytics` buckets on and what the Schedule's cover dates print; the loan's
 * `created_at` is when a clerk typed it, and 212 of 308 production rows differ on the two.
 */
export const PREMIUM_YEARS_SELECT =
    'id, amount, interest_rate, ' +
    'insurance_loan_deposits(transactions(amount)), ' +
    'insurance_loan_instalments(id), ' +
    'insurance_contracts(contract_id, ' +
    'insurances(id, starts_at, vehicles(make, model, vehicle_registrations(number))))';

/** The vehicle a policy is written over. Its plate is on `vehicle_registrations`, one FK further. */
export interface PremiumVehicleEmbed {
    make?: string | null;
    model?: string | null;
    vehicle_registrations?:
        | { number?: string | null }
        | Array<{ number?: string | null }>
        | null;
}

/** The policy — the year, and the vehicle it covers. */
export interface PremiumInsuranceEmbed {
    id?: number | null;
    starts_at?: string | null;
    vehicles?: PremiumVehicleEmbed | PremiumVehicleEmbed[] | null;
}

/** The junction — the contract the year hangs off, and the policy itself. */
export interface PremiumJunctionEmbed {
    contract_id?: number | null;
    insurances?: PremiumInsuranceEmbed | PremiumInsuranceEmbed[] | null;
}

/**
 * One `insurance_loans` row as the embed hands it back.
 *
 * SPELLED OUT RATHER THAN `any`, for `boardSubjects.LoanEmbed`'s reason: two of these fields are
 * the limbs the instrument is derived from, and nothing inside an `any` can be wrong. Everything
 * but the id is optional so that a select which stopped asking for one of them fails HERE, at the
 * mapper, rather than in a cell reading a field that was never fetched.
 */
export interface PremiumLoanEmbed {
    id: number;
    amount?: number | null;
    interest_rate?: number | null;
    insurance_loan_deposits?: LoanDepositEmbed[] | null;
    insurance_loan_instalments?: Array<{ id: number }> | null;
    insurance_contracts?: PremiumJunctionEmbed | PremiumJunctionEmbed[] | null;
}

/** One premium year, as the page reads it. */
export interface PremiumYear {
    loanId: number;
    /** `A` — `insurance_loans.amount`, the premium the year was written over. */
    premium: number | null;
    /** `D` — Σ of the transactions behind this loan's deposits. */
    depositTotal: number;
    /**
     * `A − D` — what Primelane actually put in, floored at zero.
     *
     * FLOORED, UNLIKE THE ESTATE ANALYTIC. `get_contracts_analytics` deliberately carries no
     * `GREATEST` so that it stays character-for-character the insights function of the data; this
     * is a COLUMN, and a negative "Advanced" cell is a display defect rather than a reconciliation.
     * Production has 0 overpaid loans today, so the two agree on every live row.
     */
    advanced: number;
    instalmentCount: number;
    instrument: InsuranceInstrument;
    /** `insurance_loans.interest_rate`, NULL preserved — a rate nobody set is not a rate of zero. */
    rate: number | null;
    /** `insurances.starts_at` — the policy year this premium is for, as Postgres stored it. */
    policyStart: string | null;
    insuranceId: number | null;
    contractId: number | null;
    registration: string | null;
    make: string | null;
    model: string | null;
}

/**
 * PostgREST returns a to-one embed as an object and a to-many as an array, and the shape is decided
 * by the REQUEST rather than by the schema — so every to-one embed here accepts both readings,
 * exactly as `boardSubjects.first()` does.
 */
function one<T>(value: T | T[] | null | undefined): T | null {
    if (Array.isArray(value)) return value[0] ?? null;
    return value ?? null;
}

/** One embedded row, flattened. The instrument is derived here and nowhere else. */
export function premiumYearFrom(row: PremiumLoanEmbed): PremiumYear {
    const junction = one(row.insurance_contracts);
    const insurance = one(junction?.insurances);
    const vehicle = one(insurance?.vehicles);
    const registration = one(vehicle?.vehicle_registrations);

    const premium = row.amount ?? null;
    const depositTotal = depositTotalOf(row.insurance_loan_deposits);
    const instalmentCount = (row.insurance_loan_instalments ?? []).length;

    return {
        loanId: row.id,
        premium,
        depositTotal,
        advanced: premium === null ? 0 : Math.max(premium - depositTotal, 0),
        instalmentCount,
        instrument: instrumentFor({ amount: premium, depositTotal, instalmentCount }),
        rate: row.interest_rate ?? null,
        policyStart: insurance?.starts_at ?? null,
        insuranceId: insurance?.id ?? null,
        contractId: junction?.contract_id ?? null,
        registration: registration?.number ?? null,
        make: vehicle?.make ?? null,
        model: vehicle?.model ?? null,
    };
}

export function premiumYearsFrom(rows: PremiumLoanEmbed[] | null | undefined): PremiumYear[] {
    return (rows ?? []).map(premiumYearFrom);
}

/** The page's cache entry. Both tabs read it, so the whole page is one request. */
export const PREMIUM_YEARS_KEY = ['premium-years'] as const;

/**
 * The truncation guard — a short read is a wrong page, not a smaller one [R11].
 *
 * THE GUARD LIVES IN `src/lib/fetchAllRows.ts` now, beside the paged read that shares it, and the
 * reasoning is there. This binding keeps the name both ledgers import and the subject this file is
 * about, so the premium-years callers still say nothing extra and the deposit ledger still names
 * its own table.
 */
export function assertComplete(
    rows: readonly unknown[],
    count: number | null | undefined,
    subject: string = 'premium years',
): void {
    assertReadComplete(rows, count, subject);
}

/**
 * Every premium year in the estate, in one request.
 *
 * WHOLE-TABLE, AND THAT IS THE POINT [D-B46 R9]. The instrument is `SUM(embedded deposits) >=
 * insurance_loans.amount − 0.005` — a correlated aggregate compared against a sibling column, which
 * PostgREST has no `select`/`filter` syntax for and `!inner` cannot test. So the filter has to be
 * client-side, and once it is, the old paginated read's separate `head: true` count is a liability:
 * a count that shares no filter with the page query is how `totalPages` starts lying, and the dead
 * search box (typing reset the page and did nothing else) was that bug already latent.
 *
 * THE CEILING IS THE PROJECT'S API **Max rows** SETTING, NOT ANYTHING SQL CAN SEE [R11]. Supabase
 * caps every PostgREST response at that value (Settings → API; PostgREST's `db-max-rows`, default
 * 1,000 unless the project has changed it) — it is not a `pgrst.*` GUC a query can read back, and
 * it truncates SILENTLY: the rows simply stop, with no error and no marker on the response.
 * Production holds 308 premium years (26 deposit rows, 48 instalment rows — a few tens of KB), so
 * today the whole-table read has roughly 690 rows of headroom under the default. Past that the
 * answer is pagination, or a database view exposing `deposit_total` / `instalment_count` for
 * PostgREST to filter on — not a bigger fetch.
 *
 * SO THE READ ASKS THE SERVER HOW MANY THERE ARE. `count: 'exact'` is computed over the whole
 * matching set rather than over the rows that came back, which makes `rows.length < count` exactly
 * the truncation the setting causes — and `assertComplete` turns it into a thrown error, on the
 * error path both tabs render, rather than into a quietly smaller estate.
 */
export async function fetchPremiumYears(): Promise<PremiumYear[]> {
    const { data, error, count } = await supabase
        .from('insurance_loans')
        .select(PREMIUM_YEARS_SELECT, { count: 'exact' })
        // Newest first, by the loan's own id — a stable server order the client sort then refines
        // by policy start. Unordered rows renumber themselves between two renders of one page.
        .order('id', { ascending: false })
        .returns<PremiumLoanEmbed[]>();
    if (error) throw error;
    const rows = data ?? [];
    assertComplete(rows, count);
    return premiumYearsFrom(rows);
}

/**
 * Newest policy year first — the order the table's first column is sorted by.
 *
 * TIE-BROKEN ON THE LOAN ID, and a row with no policy start sinks rather than floating: a year
 * nobody dated is not the most recent year. Sorts a copy; the query cache's array is not the
 * caller's to reorder.
 */
export function sortPremiumYears(rows: PremiumYear[]): PremiumYear[] {
    return [...rows].sort((a, b) => {
        if (a.policyStart !== b.policyStart) {
            if (!a.policyStart) return 1;
            if (!b.policyStart) return -1;
            return a.policyStart < b.policyStart ? 1 : -1;
        }
        return b.loanId - a.loanId;
    });
}

/**
 * What the search box searches — the things the table actually prints, ONE FIELD AT A TIME.
 *
 * PUNCTUATION AND CASE ARE STRIPPED FROM BOTH SIDES, because a Ghanaian plate is stored with spaces
 * ("GB 1675 21") and typed without them as often as with. The ids are matched the same way, so a
 * `#158` typed straight out of the Contract column finds contract 158. The policy start is not
 * searched: a date column is what the sort is for.
 *
 * NOT ONE CONCATENATED HAYSTACK. Stripping the separators out of a joined string lets a needle
 * straddle two fields that have nothing to do with each other: policy 9 beside contract 42 answers
 * "942", and plate "GB 1675 21" beside make "Toyota" answers "21toyota". Neither is anything an
 * admin typed looking for. Matching per field keeps the strip inside the field it belongs to.
 */
function searchableFields(row: PremiumYear): string[] {
    return [
        row.registration,
        row.make,
        row.model,
        row.insuranceId === null ? null : String(row.insuranceId),
        row.contractId === null ? null : String(row.contractId),
    ].filter((part): part is string => Boolean(part));
}

const normalise = (value: string) => value.toLowerCase().replace(/[^a-z0-9]/g, '');

export function matchesPremiumSearch(row: PremiumYear, term: string): boolean {
    const needle = normalise(term);
    if (!needle) return true;
    return searchableFields(row).some((field) => normalise(field).includes(needle));
}

/** The Instrument Select's three positions. */
export type PremiumInstrumentFilter = 'all' | 'advance' | 'paid-in-full';

/**
 * The Instrument filter, over ANYTHING CARRYING AN INSTRUMENT.
 *
 * TAKES THE ONE FIELD IT READS, NOT A `PremiumYear`. The Deposits page draws the same three-position
 * Select over the same two shapes of money, and a deposit row cannot satisfy a premium year's
 * thirteen members — it has no `advanced`, no `policyStart`, no `depositTotal` of its own. Narrowing
 * the parameter to `{ instrument }` is what makes the badge on the two pages one rule rather than
 * two that agree today; widening the ROW type instead would let a row with no instrument through.
 */
export function matchesInstrumentFilter(
    row: { instrument: InsuranceInstrument },
    filter: PremiumInstrumentFilter,
): boolean {
    if (filter === 'all') return true;
    return filter === 'advance'
        ? row.instrument === 'ADVANCE'
        : row.instrument === 'PAYMENT_RECORD';
}

/**
 * The policy year's Ghana calendar day, as Postgres stored it.
 *
 * READ OFF THE STRING, never through `new Date(...)`. `insurances.starts_at` is a `timestamptz`
 * that PostgREST serialises in UTC, and UTC is Accra — so the first ten characters ARE the business
 * day, while re-reading the instant in the viewer's zone moves every row within the offset of a
 * month boundary into the wrong bucket.
 */
export function policyDayOf(row: PremiumYear): string | null {
    return row.policyStart ? row.policyStart.substring(0, 10) : null;
}

/**
 * Rows whose policy year opens inside an inclusive 'yyyy-MM-dd' window. Undated rows are out.
 *
 * AND `insurances.starts_at` IS NULLABLE, so an undated year is a row Records lists with a "-" in
 * its first column while all four Analytics cards are blind to it at every range, all-time
 * included — there is no window a NULL is inside. Production has 0 such rows today; if one is ever
 * created the tabs will disagree by exactly it, and the fix is a date on the policy.
 */
export function withinPolicyWindow(row: PremiumYear, fromDate: string, toDate: string): boolean {
    const day = policyDayOf(row);
    return day !== null && day >= fromDate && day <= toDate;
}

/** One month of the estate's premium volume, split by instrument. */
export interface PremiumMonthPoint {
    /** 'yyyy-MM', the Ghana calendar month of `insurances.starts_at`. */
    monthKey: string;
    /** Σ (A − D) over that month's Advances — the premium Primelane financed. */
    financed: number;
    /** Σ A over that month's Records — the premium the driver paid in full. */
    paidInFull: number;
}

/**
 * The monthly series, on the policy-start basis.
 *
 * TWO SERIES RATHER THAN ONE, because one bar summing both instruments is the page's original
 * defect drawn as a chart. `monthKeys` comes from the picker's range so an empty month still draws
 * a zero, and a row whose month is outside that list is dropped — the window filter and the buckets
 * agree by construction.
 */
export function premiumMonthly(rows: PremiumYear[], monthKeys: string[]): PremiumMonthPoint[] {
    const buckets = new Map<string, PremiumMonthPoint>();
    for (const monthKey of monthKeys) {
        buckets.set(monthKey, { monthKey, financed: 0, paidInFull: 0 });
    }
    for (const row of rows) {
        const key = businessMonthKey(row.policyStart);
        const bucket = key ? buckets.get(key) : undefined;
        if (!bucket) continue;
        if (row.instrument === 'PAYMENT_RECORD') bucket.paidInFull += row.premium ?? 0;
        else bucket.financed += row.advanced;
    }
    return monthKeys.map((monthKey) => buckets.get(monthKey)!);
}

/** What the four cards state. */
export interface PremiumSummary {
    /** Σ (A − D) over Advances — the premium Primelane financed. */
    totalAdvanced: number;
    advanceCount: number;
    /** Σ A over Records — the premium the driver paid in full. */
    paidInFullTotal: number;
    paidInFullCount: number;
    /**
     * The mean rate ACROSS ADVANCES ONLY.
     *
     * A Record's rate accrues nothing — `instrumentFor` has no rate limb precisely because the
     * deposit is what makes the page true — so averaging Records in states a cost of borrowing over
     * years nobody borrowed for. 10 of the 12 production Records carry 0 or NULL and drag the mean
     * down by about 3 points. A NULL rate counts as zero, which is how the server COALESCEs it.
     */
    avgRate: number;
    rowCount: number;
}

export function premiumSummary(rows: PremiumYear[]): PremiumSummary {
    const advances = rows.filter((row) => row.instrument === 'ADVANCE');
    const records = rows.filter((row) => row.instrument === 'PAYMENT_RECORD');
    const rateTotal = advances.reduce((sum, row) => sum + (row.rate ?? 0), 0);

    return {
        totalAdvanced: advances.reduce((sum, row) => sum + row.advanced, 0),
        advanceCount: advances.length,
        paidInFullTotal: records.reduce((sum, row) => sum + (row.premium ?? 0), 0),
        paidInFullCount: records.length,
        avgRate: advances.length > 0 ? rateTotal / advances.length : 0,
        rowCount: rows.length,
    };
}

/** The two words a badge prints. NOT `INSTRUMENT_TITLES` — see below. */
export const PREMIUM_INSTRUMENT_LABELS: Record<InsuranceInstrument, string> = {
    /*
     * A TABLE CELL NAMES A SHAPE OF MONEY, NOT A DOCUMENT. `INSTRUMENT_TITLES` are the published
     * `agreement_templates.title` strings verbatim, and `instrument.ts` keeps them that way because
     * an EMPTY slot has to be named the same words the generated document will arrive with. Nothing
     * on this page is about to raise a document; thirty characters in a nine-column row would read
     * worse and would couple a ledger label to a template's title.
     */
    ADVANCE: 'Advance',
    PAYMENT_RECORD: 'Paid in full',
};

/**
 * The Rate cell — blank rather than a number that would state something untrue.
 *
 * THREE KINDS OF ROW PRINT NOTHING. A Record's rate accrues nothing at all (7 of the 12 carry 0 and
 * 3 carry NULL), a rate nobody set is not a rate of zero, and an advance at 0% is charging nothing
 * — in all three the honest cell is empty. Only a rate actually costing the driver money is
 * printed.
 */
export function premiumRateLabel(row: PremiumYear): string {
    if (row.instrument === 'PAYMENT_RECORD') return '';
    if (row.rate === null || row.rate === 0) return '';
    return `${row.rate}%`;
}
